// TODO: Review AI Code for feature: Process Map - Entire file is AI-generated
import { useReducer, useRef, useCallback, useMemo, useState, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { Map, AlertTriangle, X, Focus } from 'lucide-react';
import { useProcessMap } from '@/api/queries/useProcessMap';
import { useResetProcessMapPositions } from '@/api/mutations/useProcessMapPositions';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { useProcessChainsList } from '@/api/queries/useProcessChains';
import { useDepartments } from '@/api/queries/useDepartments';
import { buildSortedFlatDepartmentList } from '@/lib/department-utils';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePermissions } from '@/hooks/usePermissions';
import { ProcessTabsNav } from '@/components/processes/ProcessTabsNav';
import { Skeleton, Badge, Page, PageContent, VisualizationFullscreenOverlay } from '@bliks-io/ui';
import {
  VisualizationToolbar,
  VisualizationFallback
} from '@/pages/organization/systems/components/VisualizationControls';
import {
  KpiCards,
  DepartmentFilterControl,
  ChainFilterControl,
  ProcessMapSearchControl,
  ProcessMapConfirmDialogs,
  useProcessMapSearch
} from './ProcessMapControls';
import { initialUIState, processMapUIReducer } from './processMapUIState';
import { ProcessMapHeader } from './components/ProcessMapHeader';
const ProcessMap2D = lazy(() => import('./components/ProcessMap2D'));
import type { EditorActions } from './components/ProcessMap2D';

export function ProcessMapPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useProcessMap();
  const { data: allDepartments } = useDepartments();
  const { can } = usePermissions();
  const canEdit = can('ProcessMap', 'update');
  const [ui, dispatch] = useReducer(processMapUIReducer, initialUIState);
  const {
    isFullscreen,
    isEditorMode,
    selectedDepartments,
    departmentFilterMode,
    deptSearch,
    selectedChains,
    chainFilterMode,
    showResetConfirm,
    showDiscardConfirm,
    showKpis,
    dismissedHint
  } = ui;
  const { data: allChains } = useProcessChainsList();
  const resetRef = useRef<(() => void) | null>(null);
  const editorActionsRef = useRef<EditorActions | null>(null);
  const resetPositions = useResetProcessMapPositions();
  const navigate = useNavigate();
  const { focusProcessId } = useSearch({ from: '/$slug/processes/process-map' });

  const {
    searchQuery,
    setSearchQuery,
    searchMatchIds,
    focusedSearchIndex,
    focusNodeId,
    goToNextMatch,
    goToPrevMatch,
    handleSearchKeyDown
  } = useProcessMapSearch(data, focusProcessId);

  const focusProcessName = useMemo(() => {
    if (!focusProcessId || !data) return null;
    return data.processes.find((p) => p._id === focusProcessId)?.name ?? null;
  }, [focusProcessId, data]);

  const dismissFocus = useCallback(() => {
    navigate({ search: {} as never, replace: true });
    setTimeout(() => resetRef.current?.(), 100);
  }, [navigate]);

  const hasCustomPositions = useMemo(() => {
    if (!data) return false;
    return data.processes.some((p) => p.mapPosition != null);
  }, [data]);

  const handleResetConfirm = useCallback(() => {
    resetPositions.mutate(undefined, {
      onSuccess: () => {
        editorActionsRef.current?.clearPending();
        dispatch({ type: 'SET_SHOW_RESET_CONFIRM', value: false });
      },
      onError: (error) => toastApiError(error, 'errors.unknown')
    });
  }, [resetPositions]);

  const processMapDeptIds = useMemo(() => {
    if (!data) return new Set<string>();
    const ids = new Set<string>();
    for (const proc of data.processes) {
      if (proc.department?._id) ids.add(proc.department._id);
    }
    return ids;
  }, [data]);

  const sortedDepartments = useMemo(() => {
    if (!allDepartments) return [];
    const relevant = allDepartments.filter((d) => processMapDeptIds.has(d._id));
    return buildSortedFlatDepartmentList(relevant);
  }, [allDepartments, processMapDeptIds]);

  const filteredDepartments = useMemo(() => {
    if (!deptSearch) return sortedDepartments;
    const q = deptSearch.toLowerCase();
    return sortedDepartments.filter((item) => {
      const dept = item.department;
      return (
        dept.name.toLowerCase().includes(q) || (dept.abbreviation?.toLowerCase() || '').includes(q)
      );
    });
  }, [sortedDepartments, deptSearch]);

  const chainsWithProcesses = useMemo(() => {
    if (!allChains) return [];
    return allChains.filter((c) => c.processCount > 0);
  }, [allChains]);

  const handleChainChange = useCallback((value: string | string[] | undefined) => {
    dispatch({ type: 'SET_SELECTED_CHAINS', value: Array.isArray(value) ? value : [] });
  }, []);

  const selectedChainTriggerLabel = useMemo(() => {
    if (selectedChains.length === 0) return null;
    if (selectedChains.length === 1 && allChains) {
      const chain = allChains.find((c) => c._id === selectedChains[0]);
      if (chain) return chain.name;
    }
    return t('processMap.chainFilter.selected', { count: selectedChains.length });
  }, [selectedChains, allChains, t]);

  useDocumentTitle('pageTitles.processMap');

  const toggleFullscreen = useCallback(() => dispatch({ type: 'TOGGLE_FULLSCREEN' }), []);
  const handleReset = useCallback(() => {
    resetRef.current?.();
  }, []);
  const enterEditorMode = useCallback(() => dispatch({ type: 'SET_EDITOR_MODE', value: true }), []);
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const handleEditorSave = useCallback(async () => {
    setIsSavingLayout(true);
    try {
      await editorActionsRef.current?.save();
      if (!editorActionsRef.current?.hasPendingChanges()) {
        dispatch({ type: 'SET_EDITOR_MODE', value: false });
      }
    } catch (error) {
      toastApiError(error, 'errors.unknown');
    } finally {
      setIsSavingLayout(false);
    }
  }, []);
  const handleEditorDiscard = useCallback(() => {
    const hasPending = editorActionsRef.current?.hasPendingChanges();
    if (hasPending) dispatch({ type: 'SET_SHOW_DISCARD_CONFIRM', value: true });
    else dispatch({ type: 'SET_EDITOR_MODE', value: false });
  }, []);
  const handleDiscardConfirm = useCallback(() => {
    editorActionsRef.current?.discard();
    dispatch({ type: 'DISCARD_CONFIRMED' });
  }, []);
  const toggleDepartmentFilterMode = useCallback(
    () => dispatch({ type: 'TOGGLE_DEPARTMENT_FILTER_MODE' }),
    []
  );
  const toggleChainFilterMode = useCallback(
    () => dispatch({ type: 'TOGGLE_CHAIN_FILTER_MODE' }),
    []
  );
  const handleDepartmentChange = useCallback((value: string | string[] | undefined) => {
    dispatch({ type: 'SET_SELECTED_DEPARTMENTS', value: Array.isArray(value) ? value : [] });
  }, []);

  const selectedDeptTriggerLabel = useMemo(() => {
    if (selectedDepartments.length === 0) return null;
    if (selectedDepartments.length === 1 && allDepartments) {
      const dept = allDepartments.find((d) => d._id === selectedDepartments[0]);
      if (dept) {
        return (
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="truncate">{dept.name}</span>
            {dept.abbreviation && (
              <Badge variant="secondary" className="shrink-0 text-white text-3xs px-1 py-0">
                {dept.abbreviation}
              </Badge>
            )}
          </div>
        );
      }
    }
    return t('processMap.departmentFilter.selected', { count: selectedDepartments.length });
  }, [selectedDepartments, allDepartments, t]);

  if (isLoading) {
    return (
      <Page>
        <ProcessTabsNav />
        <PageContent>
          <div className="space-y-6">
            <Skeleton className="w-64 h-8" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-28 rounded-lg" />
              ))}
            </div>
            <Skeleton className="w-full h-[500px] rounded-lg" />
          </div>
        </PageContent>
      </Page>
    );
  }

  if (!data) {
    return (
      <Page>
        <ProcessTabsNav />
        <PageContent>
          <p className="text-muted-foreground">{t('common.errorOccurred')}</p>
        </PageContent>
      </Page>
    );
  }

  const renderVisualization = (fullscreen: boolean) => (
    <Suspense fallback={<VisualizationFallback height={500} />}>
      <ProcessMap2D
        data={data}
        onResetRef={resetRef}
        editorActionsRef={editorActionsRef}
        isFullscreen={fullscreen}
        isEditorMode={isEditorMode}
        selectedDepartments={selectedDepartments}
        departmentFilterMode={departmentFilterMode}
        selectedChains={selectedChains}
        chainFilterMode={chainFilterMode}
        searchQuery={searchQuery}
        focusNodeId={focusNodeId}
        focusProcessId={focusProcessId}
      />
    </Suspense>
  );

  const searchControl = (
    <ProcessMapSearchControl
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      handleSearchKeyDown={handleSearchKeyDown}
      searchMatchIds={searchMatchIds}
      focusedSearchIndex={focusedSearchIndex}
      goToPrevMatch={goToPrevMatch}
      goToNextMatch={goToNextMatch}
    />
  );

  const departmentControls = (
    <DepartmentFilterControl
      sortedDepartments={sortedDepartments}
      filteredDepartments={filteredDepartments}
      selectedDepartments={selectedDepartments}
      selectedDeptTriggerLabel={selectedDeptTriggerLabel}
      handleDepartmentChange={handleDepartmentChange}
      deptSearch={deptSearch}
      setDeptSearch={(s: string) => dispatch({ type: 'SET_DEPT_SEARCH', value: s })}
      departmentFilterMode={departmentFilterMode}
      toggleDepartmentFilterMode={toggleDepartmentFilterMode}
    />
  );

  const chainControls = (
    <ChainFilterControl
      chainsWithProcesses={chainsWithProcesses}
      selectedChains={selectedChains}
      selectedChainTriggerLabel={selectedChainTriggerLabel}
      handleChainChange={handleChainChange}
      chainFilterMode={chainFilterMode}
      toggleChainFilterMode={toggleChainFilterMode}
    />
  );

  return (
    <>
      <Page>
        <ProcessTabsNav />
        <PageContent className="flex flex-col overflow-hidden!">
          <div className="flex flex-col flex-1 min-h-0 gap-3">
            <ProcessMapHeader
              showKpis={showKpis}
              isEditorMode={isEditorMode}
              canEdit={canEdit}
              hasCustomPositions={hasCustomPositions}
              isSaving={isSavingLayout}
              onToggleKpis={() => dispatch({ type: 'TOGGLE_KPIS' })}
              onRequestResetLayout={() => dispatch({ type: 'SET_SHOW_RESET_CONFIRM', value: true })}
              onEditorDiscard={handleEditorDiscard}
              onEditorSave={handleEditorSave}
              onEnterEditorMode={enterEditorMode}
            />

            {showKpis && (
              <div className="shrink-0">
                <KpiCards summary={data.summary} />
              </div>
            )}

            {data.summary.uncategorizedProcesses > 0 && !dismissedHint && (
              <div className="flex items-center gap-3 px-4 py-2 text-sm rounded-lg border border-warning-line bg-warning-surface text-warning-strong shrink-0">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span className="flex-1">
                  {t('processMap.uncategorizedHint', {
                    count: data.summary.uncategorizedProcesses
                  })}
                </span>
                <button
                  onClick={() => dispatch({ type: 'DISMISS_HINT' })}
                  className="shrink-0 hover:text-warning-strong"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="flex-1 min-h-0 rounded-lg border bg-card shadow-xs overflow-hidden relative">
              {renderVisualization(false)}
              {focusProcessName && (
                <div className="absolute top-3 left-3 z-10">
                  <Badge
                    variant="secondary"
                    className="gap-1.5 px-3 py-1.5 bg-primary/10 text-primary border-primary/20 hover:bg-primary/15 cursor-pointer"
                    onClick={dismissFocus}
                  >
                    <Focus className="w-3.5 h-3.5" />
                    {t('processMap.focusBadge', { name: focusProcessName })}
                    <X className="w-3.5 h-3.5 ml-1" />
                  </Badge>
                </div>
              )}
              <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
                {!isEditorMode && searchControl}
                {!isEditorMode && chainControls}
                {!isEditorMode && departmentControls}
                <VisualizationToolbar
                  show3DControls={false}
                  isFullscreen={false}
                  isRotating={false}
                  onToggleRotation={() => {}}
                  onReset={handleReset}
                  onToggleFullscreen={toggleFullscreen}
                />
              </div>
            </div>
          </div>
        </PageContent>
      </Page>

      {isFullscreen && (
        <VisualizationFullscreenOverlay
          icon={Map}
          title={t('processMap.title')}
          onClose={() => dispatch({ type: 'SET_FULLSCREEN', value: false })}
          controls={
            <div className="flex items-center gap-2">
              {focusProcessName && (
                <Badge
                  variant="secondary"
                  className="gap-1.5 px-3 py-1.5 bg-primary/10 text-primary border-primary/20 hover:bg-primary/15 cursor-pointer"
                  onClick={dismissFocus}
                >
                  <Focus className="w-3.5 h-3.5" />
                  {t('processMap.focusBadge', { name: focusProcessName })}
                  <X className="w-3.5 h-3.5 ml-1" />
                </Badge>
              )}
              {searchControl}
              {chainControls}
              {departmentControls}
              <VisualizationToolbar
                show3DControls={false}
                isFullscreen
                isRotating={false}
                onToggleRotation={() => {}}
                onReset={handleReset}
                onToggleFullscreen={toggleFullscreen}
              />
            </div>
          }
        >
          {renderVisualization(true)}
        </VisualizationFullscreenOverlay>
      )}

      <ProcessMapConfirmDialogs
        showResetConfirm={showResetConfirm}
        setShowResetConfirm={(open: boolean) =>
          dispatch({ type: 'SET_SHOW_RESET_CONFIRM', value: open })
        }
        handleResetConfirm={handleResetConfirm}
        resetPending={resetPositions.isPending}
        showDiscardConfirm={showDiscardConfirm}
        setShowDiscardConfirm={(open: boolean) =>
          dispatch({ type: 'SET_SHOW_DISCARD_CONFIRM', value: open })
        }
        handleDiscardConfirm={handleDiscardConfirm}
      />
    </>
  );
}
